import memoize from 'lodash/memoize'

import { WorkflowFilterStatus } from '@app/shared/types/sf-data'

/**
 * Get the active overview filter. Rails interpolates the filter in a hidden input field value.
 * @param fallback - The default filter to use if no filter is found
 * @returns WorkflowFilterStatus - The filter to use
 */
export const getOverviewFilter = (
  fallback = 'show-all'
): WorkflowFilterStatus => {
  const overview =
    window?.Silverfin?.currentUserPreferences?.overview_filter || {}
  return overview?.[window?.Silverfin?.currentCompanyId]?.filter || fallback
}

/**
 * Returns the current stack id from the page. Rails interpolates the stack id in a hidden input field value.
 * It's unlikely that this will change for the current user per page, so memoize it
 * @returns {string | null} The stack id or null if not found
 */
export const getStackId = memoize((): string | null => {
  const stackIdElement = document.querySelector(
    '#sf_stack_id'
  ) as HTMLInputElement

  return stackIdElement?.value || null
})

/**
 * Returns whether the current workflow is locked.
 * @returns {boolean}
 */
export const isCurrentWorkflowLocked = (): boolean => {
  return window?.Silverfin?.currentWorkflowLocked() || false
}

/**
 * Returns whether the current firm supports the given feature.
 * @param feature - The feature identifier to check
 * @returns { boolean } - Whether the feature is supported
 */
export const isFeatureSupported = (
  feature: keyof typeof window.Silverfin.currentFirmFeatures
): boolean | undefined => {
  return !!window?.Silverfin?.currentFirmFeatures?.[feature]
}

/**
 * Returns whether the current company is a personal company. Rails interpolates this as a boolean in a hidden input field value.
 */
export const isPersonalCompany = memoize((_companyId: string): boolean => {
  const personalCompanyIdElement = document.querySelector(
    '#current_company_personal'
  ) as HTMLInputElement

  return personalCompanyIdElement?.value === 'true'
})

export const getCompanyName = (): string | null => {
  const companyName = document.querySelector(
    '#current_company_name'
  ) as HTMLInputElement

  return companyName?.value || null
}

export const getCurrentUserName = (): string | null => {
  const userName = document.querySelector(
    '#current_user_name'
  ) as HTMLInputElement

  return userName?.value || null
}

export const getCurrentUserId = (): string | null => {
  const userId = document.querySelector('#current_user_id') as HTMLInputElement

  return userId?.value || null
}

export const companyUrl = memoize((companyId: string): string => {
  return `/f/${window.Silverfin.currentFirmId}/${companyId}`
})
